// @ts-nocheck

// App
import React, { useEffect, useState } from 'react';
import * as S from './styles';

// Components
import PriceInput from 'app/shared/modules/MobileSRP/Header/FilterModules/PriceFilter/PriceInput';

// Misc / utils
import VALUE_CONSTANTS from 'app/shared/constants/ValueConstants';
import { PRICE_SLIDER_MAX } from 'app/shared/constants/FilterConstants';
import numberUtils from 'app/shared/utils/numberUtils';

const isEqual = (prevProps, nextProps) => {
  if (
    prevProps.histogramData &&
    prevProps.histogramData.maxCount === nextProps.histogramData &&
    nextProps.histogramData.maxCount &&
    prevProps.histogramData &&
    prevProps.histogramData.median === nextProps.histogramData &&
    nextProps.histogramData.median &&
    prevProps.minPrice === nextProps.minPrice &&
    prevProps.maxPrice === nextProps.maxPrice
  ) {
    return true;
  }
  return false;
};

const HistogramSlider = React.memo(
  ({
    ariaLabelledBy = undefined,
    disabled = undefined,
    histogramData = {},
    minPrice = null,
    maxPrice = null,
    handlePriceChange = undefined,
    role = undefined,
  }) => {
    const [sliderValue, setSliderValue] = useState([
      minPrice !== null ? minPrice : 0,
      maxPrice !== null ? maxPrice : PRICE_SLIDER_MAX,
    ]);
    const HUNDRED_TO_ONE_SCALE = VALUE_CONSTANTS.PRICE_FILTER_INCREMENT;
    const maxCount = histogramData.maxCount;
    const median = histogramData.median;

    useEffect(() => {
      const isResetCondition = minPrice === null && maxPrice === null;
      if (isResetCondition) {
        setSliderValue([minPrice !== null ? minPrice : 0, maxPrice !== null ? maxPrice : PRICE_SLIDER_MAX]);
      }
    }, [minPrice, maxPrice]);

    // Slider handles (aka Thumbs)
    const Thumb = (props) => {
      props.style = {
        ...props.style,
        zIndex: 2,
      };
      return <S.SliderThumb {...props}>{'| |'}</S.SliderThumb>;
    };

    // Slider track
    const Track = (props, state) => {
      return <S.SliderTrack {...props} index={state.index} />;
    };

    // Slider histogram bars (aka Marks)
    const HistogramBars = (props) => {
      const currentMarker = props.key;
      const currentDataPoint = histogramData.data[currentMarker];
      const [leftThumb, rightThumb] = sliderValue;
      const shouldGrayOut = currentMarker < leftThumb || currentMarker > rightThumb;
      const shouldDisplayMedian = Math.floor(currentMarker * HUNDRED_TO_ONE_SCALE) === median;

      // Adjust location to center histogram bars over thumb handles
      props.style = {
        ...props.style,
        left: props.style.left + 15, // Half of the slider handle width
      };

      return (
        <div key={`histogram-bar-${currentMarker}-${currentDataPoint}`}>
          {shouldDisplayMedian && (
            <>
              <S.HistogramMedianText {...props} $position={props.style.left} key={`${props.key}-median-text`}>
                Median: {numberUtils.asCurrency(median)}
              </S.HistogramMedianText>
              <S.HistogramMedianLine {...props} key={`${props.key}-median-line`} />
            </>
          )}
          {currentDataPoint && (
            <S.HistogramBars
              {...props}
              $grayBarColor={shouldGrayOut}
              $height={Math.floor((currentDataPoint.count / maxCount) * 100)}
            />
          )}
        </div>
      );
    };

    const handleSliderOnAfterChange = (valArr, thumbIndex) => {
      // Only the dragged thumb's value should change; the other side keeps its committed price
      handlePriceChange(valArr, thumbIndex === 0 ? { isMinChangeOnly: true } : { isMaxChangeOnly: true });
    };

    const handleSliderOnChange = (valArr) => {
      setSliderValue(valArr);
    };

    const handleSliderMinValueChange = (val, { isValueUnset, exactDollars } = {}) => {
      const units = exactDollars != null ? exactDollars / HUNDRED_TO_ONE_SCALE : val;
      const valArr = [units, sliderValue[1]];
      setSliderValue(valArr);
      handlePriceChange(valArr, {
        isMinChangeOnly: true,
        isMinValueUnset: isValueUnset,
        isPriceSetViaInput: true,
        minExactDollars: exactDollars,
      });
    };
    const handleSliderMaxValueChange = (val, { isValueUnset, exactDollars } = {}) => {
      const units = exactDollars != null ? exactDollars / HUNDRED_TO_ONE_SCALE : val;
      const valArr = [sliderValue[0], units];
      setSliderValue(valArr);
      handlePriceChange(valArr, {
        isMaxChangeOnly: true,
        isMaxValueUnset: isValueUnset,
        isPriceSetViaInput: true,
        maxExactDollars: exactDollars,
      });
    };

    const shouldShowHistogramBars = disabled ? null : { renderMark: HistogramBars };

    return (
      <>
        <S.HistogramContainer>
          {disabled && <S.NoListingsNotice>No listings found</S.NoListingsNotice>}
          <S.HistogramSlider
            {...shouldShowHistogramBars}
            ariaLabel={['Minimum price thumb', 'Maximum price thumb']}
            ariaValuetext={(state) =>
              `Current ${state.index === 0 ? 'min' : 'max'} value: ${
                state.valueNow === 0
                  ? 'No minimum'
                  : `${state.valueNow * VALUE_CONSTANTS.PRICE_FILTER_INCREMENT} dollars`
              }`
            }
            disabled={disabled}
            marks
            min={0} // 1:100 scale
            max={PRICE_SLIDER_MAX} // 1:100 scale
            value={sliderValue} // 1:100 scale
            onChange={handleSliderOnChange}
            onAfterChange={handleSliderOnAfterChange}
            renderTrack={Track}
            renderThumb={Thumb}
          />
        </S.HistogramContainer>
        <S.InputContainer aria-labelledby={ariaLabelledBy} role={role}>
          <PriceInput
            ariaLabel={'Set minimum price'}
            disabled={disabled}
            handleSliderValueChange={handleSliderMinValueChange}
            id={'min-price-input'}
            isMinInput
            isValueUnset={minPrice === null}
            name={'min-price-input'}
            placeholder={'No min'}
            value={Math.round(sliderValue[0] * HUNDRED_TO_ONE_SCALE)}
          />
          <S.RangeText aria-hidden="true">to</S.RangeText>
          <PriceInput
            ariaLabel={'Set maximum price'}
            disabled={disabled}
            handleSliderValueChange={handleSliderMaxValueChange}
            id={'max-price-input'}
            isMaxInput
            isValueUnset={maxPrice === null}
            name={'max-price-input'}
            placeholder={'No max'}
            value={Math.round(sliderValue[1] * HUNDRED_TO_ONE_SCALE)}
          />
        </S.InputContainer>
      </>
    );
  },
  isEqual,
);

export default HistogramSlider;
